import { useRef, useState, type DragEvent, type ChangeEvent } from 'react';
import { Upload, FileSpreadsheet, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface DropZoneProps {
  label: string;
  file: File | null;
  onFile: (f: File | null) => void;
  icon?: 'clock' | 'users' | 'file';
}

export function DropZone({ label, file, onFile }: DropZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) onFile(f);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) onFile(f);
  };

  return (
    <div
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      className={cn(
        'group glass rounded-2xl border-2 border-dashed transition-all p-6 cursor-pointer hover:border-indigo-400 dark:hover:border-indigo-500 hover:shadow-card',
        dragOver
          ? 'border-indigo-500 bg-indigo-50/60 dark:bg-indigo-900/20 scale-[1.01]'
          : 'border-slate-300 dark:border-slate-600',
        file && 'border-solid border-emerald-400 bg-emerald-50/30 dark:bg-emerald-900/10',
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        className="hidden"
        onChange={handleChange}
      />
      <div className="flex items-start gap-4">
        <div
          className={cn(
            'w-12 h-12 rounded-xl flex items-center justify-center shrink-0 transition-all',
            file
              ? 'bg-gradient-to-br from-emerald-500 to-teal-500 text-white shadow-lg shadow-emerald-500/30'
              : 'gradient-primary text-white group-hover:scale-110 transition-transform',
          )}
        >
          {file ? <FileSpreadsheet size={22} /> : <Upload size={22} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-slate-800 dark:text-slate-100">{label}</div>
          {file ? (
            <div className="mt-1 flex items-center gap-2">
              <span className="text-xs text-emerald-700 dark:text-emerald-300 font-semibold truncate">
                ✓ {file.name}
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onFile(null);
                }}
                className="ml-auto p-1 rounded-md hover:bg-rose-100 dark:hover:bg-rose-900/30 text-rose-600"
                aria-label="حذف"
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              اسحب الملف هنا أو اضغط لاختيار ملف (.xlsx / .xls / .csv)
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
